import { useEffect, useRef, useState } from 'react'
import { BrowserMultiFormatReader, type IScannerControls } from '@zxing/browser'

type DetectorResult = {
  rawValue?: string
}

type BarcodeDetectorInstance = {
  detect: (source: ImageBitmapSource) => Promise<DetectorResult[]>
}

type BarcodeDetectorClass = {
  new (options?: { formats?: string[] }): BarcodeDetectorInstance
}

interface BarcodeScannerProps {
  onDetected: (value: string) => void
}

// Mounted = camera on. Unmounted = camera off. Parent controls visibility.
export const BarcodeScanner = ({ onDetected }: BarcodeScannerProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const onDetectedRef = useRef(onDetected)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    onDetectedRef.current = onDetected
  })

  useEffect(() => {
    let stream: MediaStream | null = null
    let animationId: number | null = null
    let zxingControls: IScannerControls | null = null
    let done = false

    const fireOnce = (value: string) => {
      if (done) return
      done = true
      onDetectedRef.current(value)
    }

    const cleanup = () => {
      done = true
      if (animationId !== null) cancelAnimationFrame(animationId)
      stream?.getTracks().forEach((t) => t.stop())
      zxingControls?.stop()
    }

    const getCameraErrorMessage = (err: unknown): string => {
      if (!window.isSecureContext) {
        return 'Camera needs a secure page (HTTPS). Open Booky via the ngrok https URL.'
      }
      if (!(err instanceof DOMException)) {
        return 'Unable to access camera. Check browser permissions and try again.'
      }
      if (err.name === 'NotAllowedError' || err.name === 'SecurityError') {
        return 'Camera permission was denied. Allow camera access in browser/site settings and try again.'
      }
      if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        return 'No camera was found on this device.'
      }
      if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        return 'Camera is currently in use by another app. Close other camera apps and try again.'
      }
      if (err.name === 'OverconstrainedError') {
        return 'Camera constraints were not supported on this device. Try again or use manual ISBN entry.'
      }
      return 'Unable to access camera. Check browser permissions and try again.'
    }

    const start = async () => {
      if (!window.isSecureContext) {
        setError('Camera needs a secure page (HTTPS). Open Booky via the ngrok https URL.')
        return
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('This browser does not support camera access. Use manual ISBN entry instead.')
        return
      }

      const Detector = (window as Window & { BarcodeDetector?: BarcodeDetectorClass }).BarcodeDetector

      try {
        if (Detector) {
          stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })

          if (!videoRef.current) return
          videoRef.current.srcObject = stream
          await videoRef.current.play()

          const detector = new Detector({ formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128'] })

          const scan = async () => {
            if (done) return
            try {
              if (videoRef.current) {
                const barcodes = await detector.detect(videoRef.current)
                if (barcodes.length > 0 && barcodes[0].rawValue) {
                  fireOnce(barcodes[0].rawValue)
                  return
                }
              }
            } catch {
              // keep polling
            }
            animationId = requestAnimationFrame(scan)
          }

          animationId = requestAnimationFrame(scan)
          return
        }

        if (!videoRef.current) throw new Error('Video input could not be initialized.')

        const codeReader = new BrowserMultiFormatReader()
        zxingControls = await codeReader.decodeFromConstraints(
          { video: { facingMode: { ideal: 'environment' } } },
          videoRef.current,
          (result) => {
            if (result?.getText()) fireOnce(result.getText())
          },
        )
      } catch (err) {
        setError(getCameraErrorMessage(err))
      }
    }

    void start()
    return cleanup
  }, [])

  return (
    <div className='space-y-3'>
      {error ? (
        <p className='text-sm text-destructive'>{error}</p>
      ) : (
        <>
          <p className='text-xs text-muted-foreground'>
            Point your camera at the barcode. On iPhone/Safari this uses a compatibility scanner fallback.
          </p>
          <video
            ref={videoRef}
            className='w-full rounded-md border bg-black'
            playsInline
            muted
            aria-label='Live camera scanner'
          />
        </>
      )}
    </div>
  )
}
